// Horizontal rule rendered via `border-top` on a flattened inner cell
// (font-size/line-height zeroed). Per spec/design-system.md §Primitive 10.
//
// `colSpan` (P-037.16 D-116) — when set, the outer `<td>` carries
// `colspan="N"`. Required when the divider sits inside a multi-column
// table row (e.g., event-row's link arm with title + CTA columns); without
// colspan the rule only spans column 1 and is visually cut off mid-row.
//
// `paddingTop` / `paddingBottom` (P-037.29 post-Phase-Close §item A
// revision) — independent top/bottom padding. When neither is set the
// outer `<td>` carries the shorthand `padding: ${paddingY}px 0;` (back-
// compat for every existing call site). When EITHER is set, the outer
// `<td>` carries `padding: ${paddingTop ?? paddingY}px 0 ${paddingBottom
// ?? paddingY}px 0;`. Used by event-row's `dividerAboveFlushTop` arm so
// the first event row in events-list gets a flush-top rule (0 above, 16
// below) instead of the default symmetric 16/16. The `padY` shorthand
// stays as the everyday spelling — only the flush-top arm needs the
// split.

import { html } from '../render'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

export interface DividerProps {
  color?: string
  width?: number
  paddingY?: number
  /** P-037.29 post-Phase-Close §item A revision — flush-top variant. */
  paddingTop?: number
  /** P-037.29 post-Phase-Close §item A revision — flush-top variant. */
  paddingBottom?: number
  colSpan?: number
}

export function divider({
  color = tokens.color.rule,
  width = tokens.border.ruleWidth,
  paddingY = tokens.space[4],
  paddingTop,
  paddingBottom,
  colSpan,
}: DividerProps = {}): HtmlString {
  // P-037.29 post-Phase-Close §item A — resolve the outer-cell padding
  // style. When either paddingTop/paddingBottom is explicitly set, render
  // the 4-value `padding: T 0 B 0;` form so the caller can drop one side
  // (e.g., flush-top divider above the first event row in events-list).
  // Otherwise keep the shorthand `padding: ${paddingY}px 0;` for byte-
  // compat with every pre-revision call site.
  const usingSplit = paddingTop !== undefined || paddingBottom !== undefined
  const top = paddingTop ?? paddingY
  const bot = paddingBottom ?? paddingY
  const paddingStyle = usingSplit ? `padding: ${top}px 0 ${bot}px 0;` : `padding: ${paddingY}px 0;`
  // The html kernel escapes interpolated strings, so we can't fold the
  // optional ` colspan="N"` attribute fragment into a single template via
  // `${colspanAttr}` — the embedded quotes would get HTML-encoded. Instead,
  // branch the template literal so the `colspan` attribute, when present,
  // is part of the trusted template source.
  if (colSpan !== undefined) {
    // prettier-ignore
    return html`<tr><td colspan="${colSpan}" style="${paddingStyle}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top: ${width}px solid ${color}; font-size: 0; line-height: 0;">&nbsp;</td></tr></table></td></tr>`
  }
  // prettier-ignore
  return html`<tr><td style="${paddingStyle}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top: ${width}px solid ${color}; font-size: 0; line-height: 0;">&nbsp;</td></tr></table></td></tr>`
}
